<template>
  <nav class="ak-navbar" :class="classes">
    <slot />
  </nav>
</template>

<script>
import { defineComponent, computed } from 'vue'

export default defineComponent({
  /**
   * @type {String}
   * @description This is the name of the component this will also
   * be the tag name of the component to use in templates
   * @example
   * <ak-navbar>
   *  <ak-navbar-nav>
   *    <ak-navbar-item>Title</ak-navbar-item>
   *  </ak-navbar-nav>
   *  <ak-navbar-nav>
   *    <ak-navbar-button>Click Me</ak-navbar-button>
   *  </ak-navbar-nav>
   * </ak-navbar>
   */
  name: 'AkNavbar',

  /**
   * @type {Object}
   * @description A collection of properties that may be passed into
   * the component these will be bound to the component instance and
   * may be access in the template and component methods.
   */
  props: {
    /**
     * @type {Boolean}
     * @description Determines if we should apply the flex class to
     * the elemenent.
     */
    flex: {
      type: Boolean,
      default: false,
    },

    /**
     * @type {Boolean}
     * @description Determines how the child items should align.
     */
    align: {
      validator: (o) => ['center', 'top', 'bottom'].indexOf(o) >= -1,
      type: String,
      default: 'center',
    },

    /**
     * @type {Boolean}
     * @description Determines how much padding there should be
     */
    size: {
      validator: (o) => ['tiny', 'small'].indexOf(o) >= -1,
      type: String,
      default: null,
    },

    /**
     * @type {Boolean}
     * @description Determines how much space between items
     */
    spacing: {
      validator: (o) =>
        ['flush', 'tiny', 'small', 'normal', 'large'].indexOf(o) >= -1,
      type: String,
      default: 'flush',
    },
  },

  /**
   * @param {Object} props - The properties of the component
   * @description Sets up the component and returns an object containing all of the
   * attributes for the component to use.
   */
  setup(props) {
    /**
     * @returns {Object}
     * @description Returns the classes for the root element of the
     * component
     */
    const classes = computed(() => {
      return {
        [`ak-navbar--${props.align}`]: props.align,
        [`ak-navbar--gap-${props.spacing}`]: props.spacing,
        [`ak-navbar--${props.size}`]: props.size,
      }
    })

    return {
      classes,
    }
  },
})
</script>

<style lang="scss">
@import '../../scss/_settings.scss';
.ak-navbar {
  $self: &;
  display: flex;
  align-items: center;

  &--tiny {
    padding: $spacing-tiny $spacing;
  }

  &--small {
    padding: $spacing-small $spacing;
  }

  &--top {
    align-items: flex-start;
  }
  &--bottom {
    align-items: flex-end;
  }

  &__item + &__item {
    #{$self}--gap-tiny & {
      margin-left: $spacing-tiny;
    }
    #{$self}--gap-small & {
      margin-left: $spacing-small;
    }
    #{$self}--gap-normal & {
      margin-left: $spacing;
    }
    #{$self}--gap-large & {
      margin-left: $spacing-large;
    }
  }
}
</style>
